import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import Product from "./Card";
import { useCart } from "../../context/CartContext";
import productAPI from "../../api/productApi";
import Breadcrump from "../../components/Breadcrump";
import SEO from "../../components/SEO";
import { Col, Row } from "react-bootstrap";
import { Heart, ShoppingBag } from "lucide-react";
import Auth from "../../utils/AuthUtils";
import wishlistAPI from "../../api/wishlistApi";
import { showError } from "../../utils/toastUtils";

function DetailPage() {
  const { slug } = useParams();
  const { addToCart } = useCart();
  const navigate = useNavigate();

  const thumbRef = useRef();

  const [product, setProduct] = useState(null);
  const [detail, setDetail] = useState(null);
  const [variants, setVariants] = useState([]);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [selectedAttributes, setSelectedAttributes] = useState({});
  const [displayedImages, setDisplayedImages] = useState([]);

  const [mainImage, setMainImage] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [isWishlisted, setIsWishlisted] = useState(false);
  const [showScrollIcon, setShowScrollIcon] = useState(false);
  const [viewFixVideo, setViewFixVideo] = useState(false);

  const [title, setTitle] = useState();
  const [brand, setBrand] = useState({});
  const [skuCode, setSkuCode] = useState("");
  const [category, setCategory] = useState({});
  const [description, setDescription] = useState("");

  /******************* FETCH PRODUCT *******************/
  const fetchProductDetail = async () => {
    try {
      const res = await productAPI.details(slug);

      if (res.code === 200) {
        const {
          product,
          detail,
          brand,
          category,
          variants,
          relatedProducts,
          isWishlisted,
        } = res.data;

        setProduct(product);
        setIsWishlisted(isWishlisted || false);
        setDetail(detail);
        setBrand(brand);
        setCategory(category);
        setVariants(variants || []);
        setRelatedProducts(relatedProducts || []);

        setTitle(product?.title);
        setSkuCode(product?.skuCode);
        setDescription(detail?.description);

        if (variants?.length > 0) {
          setSelectedVariant(variants[0]);
        }

        const images = [];
        if (product?.thumbnail) images.push(product.thumbnail);
        if (detail?.images?.length) images.push(...detail.images);
        if (variants?.length) {
          variants.forEach((v) => {
            if (v.images?.length) images.push(...v.images);
          });
        }

        if (images.length > 0) {
          setMainImage(images[0]);
          setDisplayedImages(images);
        }
      }
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchProductDetail();
    window.scrollTo(0, 0);
  }, [slug]);

  /******************* IMAGES *******************/
  const mergedImages = [
    ...(product?.thumbnail ? [product.thumbnail] : []),
    ...(detail?.images?.map((i) => i) || []),
    ...variants.flatMap((v) => v.images || []),
  ];

  /******************* VARIANT ATTRIBUTES *******************/
  const attributeMap = {};
  variants.forEach((variant) => {
    Object.entries(variant.attributes || {}).forEach(([key, value]) => {
      if (!attributeMap[key]) {
        attributeMap[key] = new Set();
      }
      attributeMap[key].add(value);
    });
  });

  const handleVariantClick = (key, value) => {
    const updated = {
      ...selectedAttributes,
      [key]: value,
    };

    setSelectedAttributes(updated);

    const matched = variants.find((v) =>
      Object.entries(updated).every(([k, val]) => v.attributes?.[k] === val)
    );

    if (matched) {
      setSelectedVariant(matched);
      if (matched.images?.length) {
        const variantImgs = matched.images;
        setDisplayedImages(variantImgs);
        setMainImage(variantImgs[0]);
      }
    }
  };

  useEffect(() => {
    const defaultSelected = {};
    Object.entries(attributeMap).forEach(([key, valueSet]) => {
      const values = Array.from(valueSet);
      if (values.length > 0) {
        defaultSelected[key] = values[0];
      }
    });
    setSelectedAttributes(defaultSelected);
  }, [variants]);

  const extractYouTubeID = (url) => {
    const regExp = /(?:youtube\.com.*(?:\?|&)v=|youtu\.be\/)([^&?/]+)/;
    const match = url.match(regExp);
    return match ? match[1] : null;
  };

  /******************* CART *******************/
  const handleAddToCart = (e) => {
    const action = e.currentTarget.dataset.action;
    try {
      addToCart(product._id, quantity, selectedVariant?._id);
      if (action === "buy-now") {
        navigate("/checkout");
      }
    } catch (err) {
      console.error("Add to cart error:", err);
    }
  };

  const handleWishlistToggle = async (e) => {
    e.preventDefault();
    const isLoggedIn = Auth.getToken();

    if (!isLoggedIn) {
      showError("Please log in to manage your wishlist");
      return;
    }

    try {
      const res = await wishlistAPI.addProduct({ productId: product?._id });
      if (res.code === 200) {
        setIsWishlisted((prev) => !prev);
      }
    } catch (error) {
      showError("Failed to update wishlist");
    }
  };

  if (!product) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center font-['Space_Grotesk'] text-[#2E211A]">
        <div className="animate-pulse text-xl font-medium tracking-wide text-[#A6472E]">
          Loading Masterpiece...
        </div>
      </div>
    );
  }

  const price = selectedVariant
    ? selectedVariant.price
    : detail?.pricing?.minPrice || 0;

  return (
    <div className="bg-[#FFFDF8] text-[#2E211A] font-['Space_Grotesk'] min-h-screen selection:bg-[#A6472E] selection:text-white">
      <Breadcrump title={product?.title} />
      <SEO meta={detail?.meta} />

      {/* Main Hero Section */}
      <section className="py-8 lg:py-16 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">

          {/* Left: Interactive Gallery with Animation */}
          <div className="lg:col-span-7 flex flex-col-reverse md:flex-row gap-4">
            {/* Thumbnails */}
            <div
              className="flex md:flex-col gap-3 overflow-x-auto md:overflow-y-auto max-h-[550px]"
              style={{
                scrollbarWidth: "none",
                msOverflowStyle: "none",
              }}
            >
              <style>{`
                div::-webkit-scrollbar {
                  display: none;
                }
              `}</style>
              {displayedImages.map((img, i) => (
                <div
                  key={i}
                  onClick={() => setMainImage(img)}
                  className={`relative flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden cursor-pointer border-2 transition-all duration-300 transform hover:scale-105 ${mainImage === img
                    ? "border-[#A6472E] shadow-md shadow-[#A6472E]/20"
                    : "border-[#E5D9C3] opacity-70 hover:opacity-100"
                    }`}
                >
                  <img
                    src={img}
                    alt={`thumb-${i}`}
                    className="w-full h-full object-cover"
                  />
                </div>
              ))}
            </div>

            {/* Main Image Display */}
            <div className="flex-1 relative group rounded-2xl overflow-hidden bg-[#F6EFE2] border border-[#E5D9C3] shadow-[0_10px_30px_rgba(74,47,30,0.12)]">
              {mainImage && (
                <img
                  src={mainImage}
                  alt="Main Product"
                  className="w-full h-[400px] sm:h-[500px] lg:h-[550px] object-contain transition-transform duration-700 group-hover:scale-105"
                />
              )}
              <div className="absolute top-4 left-4 bg-[#4A5B3A] text-white text-xs font-semibold px-3 py-1.5 rounded-full shadow-md tracking-wider uppercase">
                Handcrafted
              </div>
            </div>
          </div>

          {/* Right: Product Info & Actions */}
          <div className="lg:col-span-5 flex flex-col">
            <div className="flex items-center justify-between mb-3">
              {brand?.name && (
                <Link
                  to={`/brands/${brand?.slug}`}
                  className="inline-block text-[#A6472E] bg-[#F6EFE2] border border-[#E5D9C3] px-3 py-1 rounded-full text-xs font-medium tracking-wide hover:bg-[#A6472E] hover:text-white transition-all duration-300"
                >
                  {brand?.name}
                </Link>
              )}

              {/* Stock Status */}
              <div>
                {selectedVariant && selectedVariant.stock > 0 ? (
                  <span className="text-emerald-700 bg-emerald-50 border border-emerald-200 text-xs font-bold px-2.5 py-1 rounded-md">
                    In Stock
                  </span>
                ) : (
                  <span className="text-rose-700 bg-rose-50 border border-rose-200 text-xs font-bold px-2.5 py-1 rounded-md">
                    Out of Stock
                  </span>
                )}
              </div>
            </div>

            <h1 className="text-2xl sm:text-3xl font-bold font-['Bebas_Neue'] tracking-wider text-[#2E211A] mb-3">
              {title}
            </h1>

            <div className="flex items-baseline gap-4 mb-4">
              <span className="text-3xl font-extrabold text-[#A6472E]">
                ₹{selectedVariant ? selectedVariant.price : price}
              </span>
              {selectedVariant?.mrp > selectedVariant?.price && (
                <span className="text-lg text-[#6B5D53] line-through font-normal">
                  ₹{selectedVariant.mrp}
                </span>
              )}
            </div>

            {/* SKU and Category Meta */}
            {(product.skuCode || category?.name) && (
              <ul className="text-sm space-y-1.5 mb-6 text-[#6B5D53] bg-[#F6EFE2]/50 p-3.5 rounded-xl border border-[#E5D9C3]">
                {product.skuCode && (
                  <li>
                    <strong className="text-[#2E211A]">SKU:</strong> {product.skuCode}
                  </li>
                )}
                {category?.name && (
                  <li>
                    <strong className="text-[#2E211A]">Category:</strong>{" "}
                    <Link
                      to={`/categories/${category?.slug}`}
                      className="text-[#A6472E] hover:underline"
                    >
                      {category.name}
                    </Link>
                  </li>
                )}
              </ul>
            )}

            {/* Hurry Up Urgency Tag */}
            {selectedVariant && selectedVariant.stock <= 100 && selectedVariant.stock > 0 && (
              <div className="mb-4 bg-amber-50 border border-amber-200 p-3 rounded-xl flex items-center gap-2 animate-bounce">
                <span className="text-rose-600 font-bold text-xs tracking-wider uppercase">
                  ⚡ Hurry Up!
                </span>
                <span className="text-xs text-[#2E211A]">
                  Only <strong className="text-rose-600">{selectedVariant.stock}</strong> items left in stock!
                </span>
              </div>
            )}

            <hr className="border-[#E5D9C3] mb-6" />

            {detail?.shortDescription && (
              <p className="text-[#6B5D53] text-sm leading-relaxed mb-6">
                {detail.shortDescription}
              </p>
            )}

            {/* Variant Options Picker */}
            <div className="space-y-4 mb-6">
              {Object.entries(attributeMap).map(([attrKey, values]) => {
                const valueArray = [...values];
                return (
                  <div key={attrKey} className="space-y-2">
                    <div className="text-xs font-bold uppercase tracking-wider text-[#2E211A]">
                      {attrKey.replace("variantAttribute", "Option")}:{" "}
                      <span className="text-[#A6472E] font-medium">
                        {selectedAttributes[attrKey] || "Select"}
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {valueArray.map((value) => (
                        <button
                          key={value}
                          type="button"
                          onClick={() => handleVariantClick(attrKey, value)}
                          className={`px-4 py-2 rounded-lg text-xs font-semibold border transition-all duration-300 ${selectedAttributes[attrKey] === value
                            ? "bg-[#A6472E] text-white border-[#A6472E] shadow-sm scale-105"
                            : "bg-white text-[#2E211A] border-[#E5D9C3] hover:border-[#A6472E]"
                            }`}
                        >
                          {value}
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Action Box: Quantity & Buttons */}
        <div className="bg-[#F6EFE2] p-4 sm:p-5 rounded-2xl border border-[#E5D9C3] shadow-sm space-y-3 sm:space-y-4">
  <label className="text-xs font-bold uppercase tracking-wider text-[#6B5D53] block">
    Select Quantity
  </label>

  <div className="flex flex-wrap sm:flex-nowrap gap-2.5 sm:gap-3 items-center">
    {/* Quantity Controls */}
    <div className="flex items-center border border-[#E5D9C3] bg-white rounded-xl overflow-hidden shadow-sm">
      <button
        onClick={(e) => {
          e.preventDefault();
          if (quantity > 1) setQuantity(quantity - 1);
        }}
        className="px-3 sm:px-4 py-2.5 text-[#2E211A] hover:bg-[#F6EFE2] transition font-bold"
      >
        -
      </button>

      <span className="px-3 sm:px-4 py-2.5 text-sm font-bold text-[#2E211A] min-w-[38px] sm:min-w-[40px] text-center">
        {quantity}
      </span>

      <button
        onClick={(e) => {
          e.preventDefault();
          setQuantity(quantity + 1);
        }}
        className="px-3 sm:px-4 py-2.5 text-[#2E211A] hover:bg-[#F6EFE2] transition font-bold"
      >
        +
      </button>
    </div>

    {/* Wishlist */}
    <button
      type="button"
      onClick={handleWishlistToggle}
      className={`flex-1 flex items-center justify-center gap-2 py-3 px-3 sm:px-4 rounded-xl border text-[11px] sm:text-xs font-bold tracking-wide uppercase transition-all duration-300 ${
        isWishlisted
          ? "bg-rose-50 border-rose-300 text-rose-600 shadow-sm"
          : "bg-white border-[#E5D9C3] text-[#2E211A] hover:border-[#A6472E]"
      }`}
    >
      <Heart
        size={16}
        className={isWishlisted ? "fill-current text-rose-600" : ""}
      />
      {isWishlisted ? "Wishlisted" : "Wishlist"}
    </button>
  </div>

  {/* Cart & Buy Buttons */}
  <div className="flex flex-row gap-2 pt-1 sm:pt-2">
    <a
      href="#shoppingCartCanvas"
      data-bs-toggle="offcanvas"
      onClick={handleAddToCart}
      data-action="cart"
      className="flex-1 flex items-center justify-center gap-1.5 sm:gap-2 bg-[#A6472E] text-white py-2.5 sm:py-3.5 px-2 sm:px-4 rounded-xl font-bold text-[10px] sm:text-sm tracking-normal sm:tracking-wider uppercase shadow-md shadow-[#A6472E]/30 hover:bg-[#7A2F1E] transition-all duration-300 no-underline whitespace-nowrap"
    >
      <ShoppingBag size={15} className="sm:w-[18px] sm:h-[18px]" />
      <span>Add to Cart</span>
    </a>

    <a
      href="#checkout"
      onClick={handleAddToCart}
      data-action="buy-now"
      className="flex-1 flex items-center justify-center bg-[#4B3226] text-white py-2.5 sm:py-3.5 px-2 sm:px-4 rounded-xl font-bold text-[10px] sm:text-sm tracking-normal sm:tracking-wider uppercase shadow-md hover:bg-[#2E211A] transition-all duration-300 no-underline whitespace-nowrap"
    >
      Buy It Now
    </a>
  </div>
</div>

            {/* Video Section Toggle / Embed */}
            {detail?.video && (
              <div className="mt-6">
                <button
                  onClick={() => setViewFixVideo(!viewFixVideo)}
                  className="text-xs font-bold text-[#A6472E] underline uppercase tracking-wider mb-2 block"
                >
                  {viewFixVideo ? "Hide Product Video" : "▶ Watch Product Video"}
                </button>
                {viewFixVideo && (
                  <div className="rounded-2xl overflow-hidden border border-[#E5D9C3] shadow-md mt-2">
                    {detail.video.includes("youtube.com") || detail.video.includes("youtu.be") ? (
                      <iframe
                        width="100%"
                        height="300"
                        src={`https://www.youtube.com/embed/${extractYouTubeID(detail.video)}`}
                        allow="autoplay; encrypted-media"
                        allowFullScreen
                        title="YouTube video"
                      ></iframe>
                    ) : (
                      <video width="100%" height="300" controls autoPlay muted>
                        <source src={detail.video} type="video/mp4" />
                        Your browser does not support HTML5 video.
                      </video>
                    )}
                  </div>
                )}
              </div>
            )}

          </div>
        </div>
      </section>

      {/* Product Highlights Section */}
      {detail?.keyFeatures?.length > 0 && (
        <section className="py-12 bg-[#F6EFE2]/60 border-y border-[#E5D9C3]">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-2xl font-bold font-['Bebas_Neue'] tracking-wider text-[#2E211A] mb-6 text-center">
              Product Highlights
            </h2>
            <div className="rounded-2xl border border-[#E5D9C3] overflow-hidden bg-white shadow-sm">
              {detail.keyFeatures.map((feature, index) => (
                <div
                  className="grid grid-cols-1 md:grid-cols-12 border-b border-[#E5D9C3] last:border-b-0"
                  key={index}
                >
                  <div className="md:col-span-4 p-4 bg-[#F6EFE2] font-bold text-xs uppercase tracking-wider text-[#2E211A] border-r border-[#E5D9C3]">
                    {feature?.label}
                  </div>
                  <div className="md:col-span-8 p-4 text-sm text-[#6B5D53]">
                    {feature?.value}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Product Description Section - Width Increased */}
      {/* <section className="py-12 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="text-2xl font-bold font-['Bebas_Neue'] tracking-wider text-[#2E211A] mb-6 text-center">
          Product Description
        </h2>
        <div
          className="prose max-w-none text-[#6B5D53] text-sm leading-relaxed bg-white  sm:p-8 rounded-2xl border border-[#E5D9C3] shadow-sm"
          dangerouslySetInnerHTML={{ __html: description }}
        />
      </section> */}

      {/* Related Products Section */}
      {relatedProducts && relatedProducts.length > 0 && (
        <section className="py-16 bg-[#F6EFE2]/40 border-t border-[#E5D9C3]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h4 className="text-2xl font-bold font-['Bebas_Neue'] tracking-wider text-[#2E211A] mb-8 text-center">
              Related Masterpieces
            </h4>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
              {relatedProducts.map((product, index) => (
                <div key={product?._id || index} className="transform hover:-translate-y-1 transition duration-300">
                  <Product item={product} />
                </div>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}

export default DetailPage;